import React from 'react';
import { FileText, Paperclip } from 'lucide-react';
import { resolveMediaUrl } from '../lib/api';

/**
 * The benefit a suggestion or kaizen claims, and its evidence.
 *
 * Records filed before benefit categories existed carry none, and they are
 * shown as "Not specified" rather than being given a category they never had.
 * Guessing one would put fabricated numbers into the very reports this was
 * added to make possible.
 */

const TONE = {
  Commercial:   'bg-[#E8EFFD] text-[#15307F]',
  Safety:       'bg-[#FDE7E9] text-[#B42318]',
  Quality:      'bg-[#E7F7EF] text-[#15654B]',
  Productivity: 'bg-[#FBF7EC] text-[#8A5A12]',
  Other:        'bg-[#EFF1F6] text-[#475467]',
};

export const formatInr = (n) =>
  n == null ? null : `₹${Number(n).toLocaleString('en-IN')}`;

export default function BenefitTag({ row, className = '' }) {
  if (!row) return null;
  const cat = row.benefit_category;
  const amt = row.benefit_amount;
  const file = row.attachment;
  // The retired sixteen-item list. Present only on records filed before the
  // five categories existed; never written to any more.
  const legacy = Array.isArray(row.benefits) ? row.benefits : [];

  return (
    <div className={`flex flex-wrap items-center gap-2 mt-1.5 ${className}`}>
      {cat ? (
        <span data-testid="benefit-tag"
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${TONE[cat] || TONE.Other}`}>
          {cat}
          {cat === 'Commercial' && amt != null && (
            <span className="font-bold"> · {formatInr(amt)}</span>
          )}
        </span>
      ) : legacy.length ? (
        /* Filed before the five categories, against the old HR-F-16 tick list.
           Those ticks are real information, so they are shown as they were
           recorded rather than reported as "not specified". */
        legacy.map((b) => (
          <span key={b} data-testid="benefit-legacy"
                className="text-[10px] px-2 py-0.5 rounded-full bg-[#EFF1F6] text-[#5A6377]">
            {b}
          </span>
        ))
      ) : (
        <span data-testid="benefit-unspecified"
              className="text-[10px] text-[#98A1B2] italic">
          Benefit: Not specified
        </span>
      )}

      {file?.url && (
        <a data-testid="benefit-attachment" href={resolveMediaUrl(file.url)}
           target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 text-[10px] text-[#2563EB] hover:underline max-w-[180px]">
          {String(file.content_type || '').startsWith('image/')
            ? <Paperclip className="w-3 h-3 flex-shrink-0" />
            : <FileText className="w-3 h-3 flex-shrink-0" />}
          <span className="truncate">{file.name || 'Attachment'}</span>
        </a>
      )}
    </div>
  );
}
